import { motion } from "framer-motion";
import { Server, Users, BookOpen, BarChart3, Code } from "lucide-react";
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const useCases = [
  { icon: Server, title: "HRMS Integration", desc: "Embed competency generation inside your existing HR management system" },
  { icon: Users, title: "ATS Integration", desc: "Auto-generate role competencies when a new job requisition is created" },
  { icon: BookOpen, title: "LMS Integration", desc: "Connect competency gaps to learning content automatically" },
  { icon: BarChart3, title: "Assessment Platforms", desc: "Use competency data to build interview and assessment content" },
];
export default function ApiPage() {
  return (
    <div>
      <section className="bg-navy pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-4xl sm:text-5xl font-bold mb-4">RoleIQ API</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-dm text-white/70 text-lg">AI-Powered Intelligence for Your Platform</motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-dm text-white/50 text-sm mt-2 max-w-xl mx-auto">Integrate RoleIQ's AI-powered competency generation directly into your HRMS, ATS, or learning platform.</motion.p>
        </div>
      </section>
      <section className="bg-cream py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl font-semibold mb-4">What the API Does</h2>
          <ul className="font-dm text-navy/70 text-sm space-y-2 list-disc pl-5">
            <li>Generate competency frameworks programmatically for any role, level, and industry</li>
            <li>Retrieve structured JSON output including competency names, KPIs, and observable skills</li>
            <li>Generate job descriptions from competency data</li>
            <li>Check and consume token balance via API calls</li>
          </ul>
        </div>
      </section>
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-playfair text-navy text-2xl text-center font-semibold mb-10">Use Cases</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((u, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-cream rounded-xl p-5 text-center">
                <u.icon className="w-8 h-8 text-gold mx-auto mb-3" />
                <h3 className="font-dm text-navy font-semibold text-sm mb-1">{u.title}</h3>
                <p className="font-dm text-navy/60 text-xs">{u.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-cream py-10">
        <div className="max-w-3xl mx-auto px-4">
          <p className="font-dm text-navy/60 text-sm text-center mb-6">API access is available on Enterprise token plans only. Each API call consumes 1 token from your organisation's token balance.</p>
        </div>
      </section>
      <section className="bg-gold py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-navy text-2xl font-bold mb-3">API in Beta — Coming Soon</h2>
          <p className="font-dm text-navy/70 text-sm mb-6">The RoleIQ API is currently in private beta. We are working with select partners to test and refine the integration experience. If you are interested in early API access, contact us.</p>
          <a href="mailto:hello@roleiq.pro?subject=Request%20API%20Access" className="inline-block bg-navy text-white font-dm font-bold px-8 py-3 rounded-lg hover:bg-navy/90 transition-colors">Request API Access — hello@roleiq.pro</a>
        </div>
      </section>
      <section className="bg-navy py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-playfair text-white text-2xl font-semibold mb-6 text-center">Documentation Preview</h2>
          <div className="bg-[#1a2a40] rounded-xl p-6 font-mono text-sm overflow-x-auto">
            <p className="text-gold mb-2">POST https://api.roleiq.pro/v1/generate</p>
            <p className="text-white/50 mb-1">Headers: Authorization: Bearer YOUR_API_TOKEN</p>
            <p className="text-white/50 mb-3">Body:</p>
            <pre className="text-white/80">{`{
  "company_name": "Acme Corp",
  "industry": "Manufacturing",
  "job_role": "Plant Supervisor",
  "level": "Mid Level",
  "company_values": "Safety First, Excellence, Integrity",
  "num_competencies": 5
}`}</pre>
            <p className="text-white/50 mt-4 mb-1">Response:</p>
            <pre className="text-green-400">{`{
  "role_summary": "...",
  "competencies": [...],
  "tokens_remaining": 47
}`}</pre>
          </div>
        </div>
      </section>
    </div>
  );
}
